/* global google */
import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { GoogleMap, useJsApiLoader } from '@react-google-maps/api';
import {
  MarkerClusterer,
  MarkerClustererEvents,
  SuperClusterViewportAlgorithm,
} from "@googlemaps/markerclusterer";
import { gmApiKey } from '../../parseConfig';

import UITopButtonsUsage from './UITopButtonsUsage';
import { getControlledMapTypeId } from '../helpers/mapOptions';
import { USAGE_CLUSTER_RADIUS, usageClusterRenderer } from '../helpers/usageMarkerClustering';
import { releaseSupersededClusterMarkers } from '../stock/releaseClusterMarkers';


const GMAPS_LIBRARIES = ['places', 'marker', 'geometry'];

const containerStyle = {
  width: '100%',
  height: '100%',
};

const defaultNZ = { lat: -41.0, lng: 173.0 };
const ANALYSIS_RADIUS_M = 50000;
const BASE_MARKER_SIZE = 17.28;
const BASE_MARKER_BORDER = 4.32;
const SELECTED_BORDER_MULTIPLIER = 1.5;

function getPinColor(style) {
  switch (style) {
    case 'SolidTimber':
      return '#00ff00';
    case 'TimberFramed':
      return '#009cff';
    case 'Accessory':
      return '#ff007f';
    case 'Transportables':
      return '#ff8c00';
    default:
      return '#596A99';
  }
}


function UsageMapComponent({
  pins = [],
  presentationModeiFrame,
  isLoading = false,
  onPinSelect = () => { },
  onForceRightSidebarOpen = () => { },
  selectedPin = null,
  onSelectionInvalidated = () => { },
  layers = { primary: [], secondary: [] },
  selectedBaseMap = 'None',
  onBaseMapChange = () => { },
  isParcelBoundariesVisible = false,
  topLayerRef = { current: null },
  onParcelToggle = () => { },
  onMapReady = () => { },
  onCircleAnalysisChange = () => { },
  mapInstanceRef = null,
}, ref) {

  const internalMapRef = useRef(null);
  const mapRef = mapInstanceRef || internalMapRef;
  const [mapReady, setMapReady] = useState(false);
  const baseOverlayRef = useRef(null);
  const analysisCircleRef = useRef(null);
  const pinsRef = useRef(pins);
  const markerEntriesRef = useRef([]);
  const selectedPinRef = useRef(selectedPin);
  const selectedPinZoomRef = useRef(null);
  const initialIdleListenerRef = useRef(null);

  const didInitialFit = useRef(false);

  const { isLoaded } = useJsApiLoader({
    id: 'google-maps',
    googleMapsApiKey: gmApiKey,
    libraries: GMAPS_LIBRARIES,
  });


  const zoomToNZ = useCallback(() => {
    if (!mapRef.current) return;
    mapRef.current.setCenter(defaultNZ);
    mapRef.current.setZoom(6);
  }, [mapRef]);

  useEffect(() => {
    pinsRef.current = pins;
  }, [pins]);

  useEffect(() => {
    selectedPinRef.current = selectedPin;
    if (selectedPin && mapRef.current) {
      selectedPinZoomRef.current = mapRef.current.getZoom();
    } else {
      selectedPinZoomRef.current = null;
    }
  }, [selectedPin, mapRef]);

  const applyMarkerSelectionStyles = useCallback(() => {
    const selectedId = selectedPinRef.current?.id || null;
    markerEntriesRef.current.forEach((entry) => {
      const isSelected = selectedId && entry.pin.id === selectedId;
      entry.icon.style.width = `${isSelected ? BASE_MARKER_SIZE * 2 : BASE_MARKER_SIZE}px`;
      entry.icon.style.height = `${isSelected ? BASE_MARKER_SIZE * 2 : BASE_MARKER_SIZE}px`;
      entry.icon.style.border = `${isSelected ? BASE_MARKER_BORDER * SELECTED_BORDER_MULTIPLIER : BASE_MARKER_BORDER}px solid #000`;
      if (entry.marker) {
        entry.marker.zIndex = isSelected ? 9999 : undefined;
      }
    });
  }, []);

  // Handle base map overlay for custom layers
  const toggleParcelBoundaries = useCallback((visible) => {
    if (!mapRef.current || !topLayerRef?.current) return;

    const existingIndex = mapRef.current.overlayMapTypes.getArray().indexOf(topLayerRef.current);

    if (visible) {
      if (existingIndex === -1) {
        mapRef.current.overlayMapTypes.push(topLayerRef.current);
      }
    } else {
      if (existingIndex > -1) {
        mapRef.current.overlayMapTypes.removeAt(existingIndex);
      }
    }
  }, [topLayerRef, mapRef]);

  const applyBaseMap = useCallback((name) => {
    if (!mapRef.current) return;

    // Clear existing overlays (base + parcel) to match main page behaviour
    mapRef.current.overlayMapTypes.clear();
    baseOverlayRef.current = null;

    if (name === 'None') {
      mapRef.current.setMapTypeId('roadmap');
      // Parcel layer handled below via toggleParcelBoundaries
      toggleParcelBoundaries(isParcelBoundariesVisible);
      return;
    }

    const layer = Array.isArray(layers.secondary)
      ? layers.secondary.find((l) => l.get('name') === name)
      : null;

    if (layer && layer.get('format')) {
      const overlay = new window.google.maps.ImageMapType({
        getTileUrl: function (coord, zoom) {
          return layer.get('format')
            .replace('{z}', zoom)
            .replace('{x}', coord.x)
            .replace('{y}', coord.y);
        },
        tileSize: new window.google.maps.Size(256, 256),
        maxZoom: 22,
        name: layer.get('name')
      });

      mapRef.current.overlayMapTypes.push(overlay);
      baseOverlayRef.current = overlay;
    }
    else if (layer) {
      switch (layer.get('name')) {
        case 'Google Maps Satellite':
          mapRef.current.setMapTypeId('satellite');
          break;
        case 'Google Maps Base Layer':
          mapRef.current.setMapTypeId('roadmap');
          break;
        default:
          mapRef.current.setMapTypeId('roadmap');
      }
    } else {
      mapRef.current.setMapTypeId('roadmap');
    }

    // Re-add/remove parcel boundaries according to current state
    toggleParcelBoundaries(isParcelBoundariesVisible);
  }, [layers.secondary, isParcelBoundariesVisible, toggleParcelBoundaries, mapRef]);


  // Render markers + cluster
  useEffect(() => {

    if (!isLoaded || !mapReady || !pins.length) {
      markerEntriesRef.current = [];
      return;
    }

    const markerClickCleanups = [];
    const entries = pins
      .filter((p) => p.coordinates)
      .map((p) => {
        const markerIcon = document.createElement('div');
        markerIcon.style.width = `${BASE_MARKER_SIZE}px`;
        markerIcon.style.height = `${BASE_MARKER_SIZE}px`;
        markerIcon.style.transform = 'translateY(50%)';
        markerIcon.style.borderRadius = '50%';
        markerIcon.style.border = `${BASE_MARKER_BORDER}px solid #000`;
        markerIcon.style.backgroundColor = getPinColor(p.style);
        markerIcon.style.boxShadow = '2px 2px 8px rgba(0,0,0,0.6)';

        const marker = new google.maps.marker.AdvancedMarkerElement({
          position: p.coordinates,
          content: markerIcon,
          title: p.planName || 'Plan drop',
          gmpClickable: true,
        });

        const handleMarkerClick = () => {
          onPinSelect(p);
          onForceRightSidebarOpen?.();
          // Pan to pin if zoomed way out
          const currentZoom = mapRef.current.getZoom();
          if (currentZoom < 19) {
            mapRef.current.setCenter(p.coordinates);
            mapRef.current.setZoom(19);
          }
          selectedPinZoomRef.current = mapRef.current.getZoom();
        };
        marker.addEventListener('gmp-click', handleMarkerClick);
        markerClickCleanups.push(() => {
          marker.removeEventListener('gmp-click', handleMarkerClick);
        });

        return { marker, icon: markerIcon, pin: p };
      });

    markerEntriesRef.current = entries;
    applyMarkerSelectionStyles();

    const clusterer = new MarkerClusterer({
      map: mapRef.current,
      markers: entries.map((entry) => entry.marker),
      algorithm: new SuperClusterViewportAlgorithm({
        radius: USAGE_CLUSTER_RADIUS,
        viewportPadding: 100,
      }),
      renderer: usageClusterRenderer,
    });

    let previousClusters = [];
    const clusteringBeginListener = clusterer.addListener(
      MarkerClustererEvents.CLUSTERING_BEGIN,
      () => {
        previousClusters = [...clusterer.clusters];
      },
    );
    const clusteringEndListener = clusterer.addListener(
      MarkerClustererEvents.CLUSTERING_END,
      () => {
        releaseSupersededClusterMarkers(previousClusters, clusterer.clusters);
        previousClusters = [];
      },
    );

    return () => {
      clusteringBeginListener.remove();
      clusteringEndListener.remove();
      markerClickCleanups.forEach((cleanup) => cleanup());
      releaseSupersededClusterMarkers(clusterer.clusters, []);
      clusterer.setMap(null);
      clusterer.clearMarkers(true);
      markerEntriesRef.current = [];
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoaded, mapReady, pins]);

  useEffect(() => {
    applyMarkerSelectionStyles();
  }, [selectedPin, applyMarkerSelectionStyles]);

  // Sync base map selection
  useEffect(() => {
    if (!mapReady) return;
    applyBaseMap(selectedBaseMap);
  }, [selectedBaseMap, mapReady, applyBaseMap, mapRef]);

  // Sync parcel boundaries
  useEffect(() => {
    if (!mapReady) return;
    toggleParcelBoundaries(isParcelBoundariesVisible);
    // mapRef is stable (useRef); lint satisfied by including it
  }, [isParcelBoundariesVisible, mapReady, toggleParcelBoundaries, mapRef]);


  // Initial fit
  useEffect(() => {
    if (!mapRef.current || !mapReady || didInitialFit.current) return;
    didInitialFit.current = true;

    requestAnimationFrame(() => {
      zoomToNZ();
    });
  }, [mapReady, zoomToNZ, mapRef]);

  const clearAnalysisCircle = useCallback(() => {
    if (analysisCircleRef.current) {
      analysisCircleRef.current.setMap(null);
      analysisCircleRef.current = null;
    }
    onCircleAnalysisChange({ hasCircle: false, topPlans: [] });
  }, [onCircleAnalysisChange]);

  useEffect(() => {
    if (!mapRef.current || !mapReady || !window.google?.maps?.geometry?.spherical) return;

    const clickListener = mapRef.current.addListener('click', (event) => {
      if (!event?.latLng) return;

      if (analysisCircleRef.current) {
        analysisCircleRef.current.setMap(null);
      }

      analysisCircleRef.current = new window.google.maps.Circle({
        strokeColor: '#FF0000',
        strokeOpacity: 0.8,
        strokeWeight: 2,
        fillColor: '#FF0000',
        fillOpacity: 0.25,
        map: mapRef.current,
        center: event.latLng,
        radius: ANALYSIS_RADIUS_M,
      });

      const planCounts = {};
      pinsRef.current
        .filter((pin) => pin?.coordinates)
        .forEach((pin) => {
          const pinLatLng = new window.google.maps.LatLng(pin.coordinates.lat, pin.coordinates.lng);
          const distanceM = window.google.maps.geometry.spherical.computeDistanceBetween(pinLatLng, event.latLng);

          if (distanceM <= ANALYSIS_RADIUS_M) {
            const key = pin.planName || 'Unnamed Plan';
            planCounts[key] = (planCounts[key] || 0) + 1;
          }
        });

      const topPlans = Object.entries(planCounts)
        .sort((a, b) => b[1] - a[1])
        .slice(0, 3)
        .map(([name, count]) => ({ name, count }));

      onCircleAnalysisChange({ hasCircle: true, topPlans });
    });

    return () => {
      clickListener.remove();
    };
  }, [mapReady, mapRef, onCircleAnalysisChange]);

  useEffect(() => {
    if (!mapRef.current || !mapReady) return;

    const listener = mapRef.current.addListener('idle', () => {
      const currentSelected = selectedPinRef.current;
      if (!currentSelected?.coordinates) return;

      const currentZoom = mapRef.current.getZoom();
      const selectedZoom = selectedPinZoomRef.current;
      const zoomOutTooFar =
        typeof selectedZoom === 'number' && typeof currentZoom === 'number' && currentZoom <= selectedZoom - 2;

      const bounds = mapRef.current.getBounds();
      const selectedLatLng = new window.google.maps.LatLng(
        currentSelected.coordinates.lat,
        currentSelected.coordinates.lng
      );
      const selectedVisible = bounds ? bounds.contains(selectedLatLng) : true;

      if (zoomOutTooFar || !selectedVisible) {
        onSelectionInvalidated();
      }
    });

    return () => {
      listener.remove();
    };
  }, [mapReady, mapRef, onSelectionInvalidated]);


  useImperativeHandle(ref, () => ({
    zoomToAll: () => zoomToNZ(),
    changeBaseMap: (name) => applyBaseMap(name),
    toggleParcelBoundaries: (visible) => toggleParcelBoundaries(visible),
    clearAnalysisCircle,
  }), [zoomToNZ, applyBaseMap, toggleParcelBoundaries, clearAnalysisCircle]);


  // Keep the Google map configuration stable while marker selection, sidebars,
  // filters, and analysis state rerender the Usage page.
  const options = useMemo(() => ({
    mapId: 'ed3c51d7a4dfee624cd8272f',
    mapTypeId: getControlledMapTypeId({
      enableLayerPicker: true,
      selectedBaseMap,
      satelliteMapTypeId: 'satellite',
    }),
    maxZoom: 21,
    minZoom: 4,
    tilt: 0,
    disableDefaultUI: true,
    clickableIcons: false,
    ...(presentationModeiFrame && { gestureHandling: 'greedy' })
  }), [presentationModeiFrame, selectedBaseMap]);


  if (!isLoaded) return null;


  return (
    <>
      <GoogleMap
        mapContainerStyle={containerStyle}
        center={defaultNZ}
        zoom={5}
        options={options}
        onLoad={(map) => {
          mapRef.current = map;
          initialIdleListenerRef.current = google.maps.event.addListenerOnce(map, "idle", () => {
            initialIdleListenerRef.current = null;
            setMapReady(true);
            onMapReady();
          });
        }}
        onUnmount={() => {
          initialIdleListenerRef.current?.remove();
          initialIdleListenerRef.current = null;
          mapRef.current = null;
        }}
      >
      </GoogleMap >

      <UITopButtonsUsage
        onZoomToNZ={zoomToNZ}
        pinCount={pins.length}
      />

      {isLoading && (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            zIndex: 1200,
            backgroundColor: 'rgba(249, 251, 252, 0.64)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            pointerEvents: 'auto',
          }}
        >
          <div
            style={{
              backgroundColor: 'rgba(255, 255, 255, 0.92)',
              color: 'var(--phblueaccent)',
              border: '1px solid rgba(89, 106, 153, 0.28)',
              borderRadius: '20px',
              padding: '20px 28px',
              boxShadow: 'var(--ph-box-shadow-hover)',
              fontWeight: 600,
              fontSize: 16,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 8,
              minWidth: 220,
            }}
          >
            <span>Loading pins...</span>
            <span style={{ fontWeight: 400, fontSize: 13, opacity: 0.82 }}>
              Please wait
            </span>
          </div>
        </div>
      )}
    </>
  );
}

export default forwardRef(UsageMapComponent);
